import { useEffect, useMemo, useState } from 'react';
import Button from '@material-ui/core/Button';
import { PiPlayCircleDuotone } from 'react-icons/pi';
import { useDispatch } from 'react-redux';

import { showMessage } from 'app/store/fuse/messageSlice';

import OptimizationPipelinePopup, { OptimizationPipelinePopupHeader } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';

import OptimizationPipelineInputTable from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelineInputTable';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';

const ENABLE_MOCK_ROWS_FALLBACK = true;

const TYPE_DISTURBANCE = 'Disturbance Variable';
const TYPE_SET_POINT = 'Set Point';

const columns = [
    {
        key: 'type',
        label: 'Type',
        readOnly: true,
        minWidth: 140,
        sortable: true
    },
    {
        key: 'attribute',
        label: 'Attribute',
        readOnly: true,
        minWidth: 220,
        sortable: true
    },
    {
        key: 'base',
        label: 'Base',
        type: 'number',
        numeric: true,
        minWidth: 160
    },
    {
        key: 'best',
        label: 'Best',
        type: 'number',
        numeric: true,
        minWidth: 160
    }
];

function parseNumber(x) {
    if (x == null) return null;

    const s = String(x).trim().replace(',', '.');

    if (s === '') return null;

    const v = Number(s);

    return Number.isFinite(v) ? v : NaN;
}

function getRandomInt(min, max) {
    const _min = Math.ceil(min);
    const _max = Math.floor(max);
    return Math.floor(Math.random() * (_max - _min + 1)) + _min;
}

function getRandomStepFloat(min, max, step) {
    const steps = Math.round((max - min) / step);
    const randomStepIndex = getRandomInt(0, steps);
    const value = min + randomStepIndex * step;
    return Number(value.toFixed(10));
}

function getRandomDynamicCheckValues() {
    return {
        HMB_2003: getRandomInt(39, 40),
        HMB_2006: getRandomInt(39, 40),
        'FIC-2001_NEW_SP': getRandomInt(24000, 26000),
        'TIC-2054_NEW_SP': getRandomInt(40, 50),
        'TIC-2053_NEW_SP': getRandomInt(45, 50),
        'FIC-2057_NEW_SP': getRandomInt(50000, 60000),
        'TIC-2052_SP_RATE': getRandomInt(46, 50),
        'FFIC-2060_SP_RATE': getRandomStepFloat(0.2, 0.3, 0.01)
    };
}

function buildMockRows() {
    const baseValues = getRandomDynamicCheckValues();
    const bestValues = getRandomDynamicCheckValues();

    return [
        {
            id: 'HMB_2003',
            type: TYPE_DISTURBANCE,
            attribute: 'HMB_2003',
            base: baseValues.HMB_2003,
            best: bestValues.HMB_2003
        },
        {
            id: 'HMB_2006',
            type: TYPE_DISTURBANCE,
            attribute: 'HMB_2006',
            base: baseValues.HMB_2006,
            best: bestValues.HMB_2006
        },
        {
            id: 'FIC-2001_NEW_SP',
            type: TYPE_SET_POINT,
            attribute: 'FIC-2001_NEW_SP',
            base: baseValues['FIC-2001_NEW_SP'],
            best: bestValues['FIC-2001_NEW_SP']
        },
        {
            id: 'TIC-2054_NEW_SP',
            type: TYPE_SET_POINT,
            attribute: 'TIC-2054_NEW_SP',
            base: baseValues['TIC-2054_NEW_SP'],
            best: bestValues['TIC-2054_NEW_SP']
        },
        {
            id: 'TIC-2053_NEW_SP',
            type: TYPE_SET_POINT,
            attribute: 'TIC-2053_NEW_SP',
            base: baseValues['TIC-2053_NEW_SP'],
            best: bestValues['TIC-2053_NEW_SP']
        },
        {
            id: 'FIC-2057_NEW_SP',
            type: TYPE_SET_POINT,
            attribute: 'FIC-2057_NEW_SP',
            base: baseValues['FIC-2057_NEW_SP'],
            best: bestValues['FIC-2057_NEW_SP']
        },
        {
            id: 'TIC-2052_SP_RATE',
            type: TYPE_SET_POINT,
            attribute: 'TIC-2052_SP_RATE',
            base: baseValues['TIC-2052_SP_RATE'],
            best: bestValues['TIC-2052_SP_RATE']
        },
        {
            id: 'FFIC-2060_SP_RATE',
            type: TYPE_SET_POINT,
            attribute: 'FFIC-2060_SP_RATE',
            base: baseValues['FFIC-2060_SP_RATE'],
            best: bestValues['FFIC-2060_SP_RATE']
        }
    ];
}

function normalizeRows(rows) {
    if (!Array.isArray(rows)) return [];

    return rows.map((row, index) => ({
        id: row?.id || row?.attribute || index,
        type: row?.type || '',
        attribute: row?.attribute || '',
        base: row?.base ?? '',
        best: row?.best ?? ''
    }));
}

export default function DynamicValidationSettingsPopup() {
    const {
        popup,
        closePopup,
        openPopup,
        ts
    } = useOptimizationPipeline();

    const dispatch = useDispatch();

    const [running, setRunning] = useState(false);
    const [rows, setRows] = useState([]);
    const [errors, setErrors] = useState({});

    const popupRows = useMemo(() => {
        return Array.isArray(popup?.rows) ? popup.rows : [];
    }, [popup]);

    useEffect(() => {
        if (popup?.type !== 'DYNAMIC_VALIDATION_SETTINGS') {
            return;
        }

        const nextRows =
            popupRows.length > 0
                ? normalizeRows(popupRows)
                : ENABLE_MOCK_ROWS_FALLBACK
                    ? buildMockRows()
                    : [];

        setRows(nextRows);
        setErrors({});
    }, [popup, popupRows]);

    function handleChange(index, key, value) {
        setRows(prev =>
            prev.map((row, i) =>
                i === index
                    ? { ...row, [key]: value }
                    : row
            )
        );

        setErrors(prev => {
            if (!prev?.[index]?.[key]) return prev;

            return {
                ...prev,
                [index]: {
                    ...prev[index],
                    [key]: ''
                }
            };
        });
    }

    function validate() {
        const nextErrors = {};
        const payload = {
            base: {},
            best: {}
        };

        rows.forEach((row, index) => {
            if (!row?.attribute) return;

            const baseValue = parseNumber(row?.base);
            const bestValue = parseNumber(row?.best);

            if (baseValue === null) {
                nextErrors[index] = { ...(nextErrors[index] || {}), base: 'Required' };
            } else if (Number.isNaN(baseValue)) {
                nextErrors[index] = { ...(nextErrors[index] || {}), base: 'Invalid number' };
            } else {
                payload.base[row.attribute] = baseValue;
            }

            if (bestValue === null) {
                nextErrors[index] = { ...(nextErrors[index] || {}), best: 'Required' };
            } else if (Number.isNaN(bestValue)) {
                nextErrors[index] = { ...(nextErrors[index] || {}), best: 'Invalid number' };
            } else {
                payload.best[row.attribute] = bestValue;
            }
        });

        setErrors(nextErrors);

        return {
            payload,
            hasError: Object.keys(nextErrors).length > 0
        };
    }

    function handleRun() {
        if (!ts) {
            dispatch(showMessage({ message: 'Missing timestamp.' }));
            return;
        }

        const { payload, hasError } = validate();

        if (hasError) {
            dispatch(showMessage({ message: 'Please fill all required values.' }));
            return;
        }

        if (Object.keys(payload.base).length === 0 || Object.keys(payload.best).length === 0) {
            dispatch(showMessage({ message: 'No values to process.' }));
            return;
        }

        setRunning(true);

        try {
         
            openPopup({
                type: 'DYNAMIC_VALIDATION',
                payload: {
                    ts,
                    ...payload
                }
            });
        } catch (error) {
            console.error('[DynamicValidationSettingsPopup] run error:', error);
            dispatch(showMessage({ message: 'Run failed.' }));
        } finally {
            setRunning(false);
        }
    }

    return (
        <OptimizationPipelinePopup
            open={popup?.type === 'DYNAMIC_VALIDATION_SETTINGS'}
            onClose={closePopup}
            width={popup?.width || '820px'}
            minWidth={popup?.minWidth || 480}
            maxWidth={popup?.maxWidth || 1440}
            className={popup?.className}
        >

            <OptimizationPipelinePopupHeader
                title='Dynamic Validation Settings'
                subtitle='Edit dynamic validation inputs'
                icon={PiPlayCircleDuotone}
                onClose={closePopup}
            />

            <OptimizationPipelineInputTable
                columns={columns}
                rows={rows}
                errors={errors}
                onChange={handleChange}
                minHeight={360}
                states={{
                    emptyTitle: 'No dynamic validation inputs available',
                    noResultsTitle: 'No matching dynamic validation rows'
                }}
                sort={{
                    key: 'attribute',
                    direction: 'asc'
                }}
                rowKey={(row, index) => row?.id || row?.attribute || index}
            />

            <div className='shrink-0 flex items-center justify-between gap-12 px-16 py-12 border-t border-gray-200 bg-gray-50'>

                <Button
                    onClick={closePopup}
                    disabled={running}
                >
                    Cancel
                </Button>

                <Button
                    variant='contained'
                    color='primary'
                    onClick={handleRun}
                    disabled={running || !rows.length || !ts}
                >
                    {running ? 'Running...' : 'Run'}
                </Button>

            </div>

        </OptimizationPipelinePopup>
    );
}